import React, { useState, useEffect } from 'react';
import { RouteFeedback } from '../../models/climbing/RouteFeedback';
import { RouteFeedbackService } from '../../services/firebase/routeFeedbackService';
import { getClimber } from '../../services/firebase/climber';
import { Climber } from '../../models/user/Climber';
import { GradeSystem, getGradeAsString } from '../../models/climbing/Grade';
import './RouteFeedbackList.css';

interface RouteFeedbackListProps {
  routeId: string;
  gymId: string;
  maxItems?: number;
  showComments?: boolean;
  showStars?: boolean;
  showGrades?: boolean;
}

interface FeedbackWithClimber extends RouteFeedback {
  climberName?: string;
}

export const RouteFeedbackList: React.FC<RouteFeedbackListProps> = ({
  routeId,
  gymId,
  maxItems,
  showComments = true,
  showStars = true,
  showGrades = true
}) => {
  const [feedbacks, setFeedbacks] = useState<FeedbackWithClimber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadFeedbacks();
  }, [routeId, gymId]);

  const loadFeedbacks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const routeFeedbacks = await RouteFeedbackService.getRouteFeedbacks(gymId, routeId);
      
      // Filter feedbacks based on what should be shown
      const filteredFeedbacks = routeFeedbacks.filter(feedback => {
        if (!showComments && !showStars && !showGrades) return false;
        
        const hasVisibleContent = 
          (showComments && feedback.comment && feedback.comment.trim().length > 0) ||
          (showStars && feedback.stars !== undefined) ||
          (showGrades && feedback.suggestedGrade !== undefined);
          
        return hasVisibleContent;
      });

      // Limit results if maxItems is specified
      const limitedFeedbacks = maxItems 
        ? filteredFeedbacks.slice(0, maxItems)
        : filteredFeedbacks;

      // Load climber names
      const feedbacksWithClimbers = await Promise.all(
        limitedFeedbacks.map(async (feedback) => {
          try {
            const climber = await getClimber(feedback.climberId);
            return {
              ...feedback,
              climberName: climber?.name || 'Anonymous'
            };
          } catch (err) {
            return {
              ...feedback,
              climberName: 'Anonymous'
            };
          }
        })
      );

      setFeedbacks(feedbacksWithClimbers);
    } catch (err) {
      setError('Failed to load feedback');
      console.error('Error loading route feedbacks:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (stars?: number) => {
    if (!showStars || stars === undefined) return null;

    return (
      <div className="feedback-item-stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${star <= stars ? 'filled' : ''}`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const renderGrade = (suggestedGrade?: number) => {
    if (!showGrades || suggestedGrade === undefined) return null;

    return (
      <div className="feedback-item-grade">
        <span className="grade-label">Suggested Grade:</span>
        <span className="grade-value">{getGradeAsString(suggestedGrade, GradeSystem.FRENCH)}</span>
      </div>
    );
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      if (diffInHours < 1) {
        return 'Just now';
      }
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 24 * 7) {
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <div className="feedback-list-loading">
        <div className="loading-spinner"></div>
        <span>Loading feedback...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="feedback-list-error">
        <span>{error}</span>
      </div>
    );
  }

  if (feedbacks.length === 0) {
    return (
      <div className="feedback-list-empty">
        <span>No feedback available</span>
      </div>
    );
  }

  return (
    <div className="route-feedback-list">
      {feedbacks.map((feedback) => (
        <div key={feedback.id} className="feedback-item">
          <div className="feedback-header">
            <span className="climber-name">{feedback.climberName}</span>
            <span className="feedback-date">{formatDate(feedback.createdAt)}</span>
          </div>
          
          <div className="feedback-content">
            {renderStars(feedback.stars)}
            {renderGrade(feedback.suggestedGrade)}
            
            {showComments && feedback.comment && feedback.comment.trim().length > 0 && (
              <div className="feedback-comment">
                <p>{feedback.comment}</p>
              </div>
            )}
          </div>
        </div>
      ))}
      
      {maxItems && feedbacks.length === maxItems && (
        <div className="feedback-list-more">
          <span>More feedback available...</span>
        </div>
      )}
    </div>
  );
};
